<div class="audio-preview-wrapper" [class.has-comments]="showCommentsSidebar">
  <!-- Reusable Header Component -->
  <app-preview-header
    [fileData]="model"
    [showMetadata]="true"
    [showDownload]="true"
    [showComments]="true"
    [commentsActive]="showCommentsSidebar"
    [showFullscreen]="true"
    [fullscreenActive]="isFullscreen"
    [showMinimize]="true"
    (comments)="toggleCommentsSidebar()"
    (fullscreen)="toggleFullscreen()"
    (minimize)="minimizeToMiniPlayer()"
    (download)="downloadMedia($event)"
    (close)="closeDialog()">
  </app-preview-header>

  <div class="audio-main">
  <!-- Audio Player Body -->
  <div class="audio-body" [class.comment-armed]="commentArmed" (click)="onAudioAreaClick($event)">
    <!-- Loading Overlay -->
    @if (isLoading) {
      <app-preview-loading
        [message]="'Loading audio...'"
        [overlay]="true">
      </app-preview-loading>
    }

    <!-- Audio Visualization Area -->
    @if (!isLoading) {
      <div class="audio-visual-area">
        <!-- Music Icon with Animation -->
        <div class="music-icon" [class.playing]="isPlaying">
          <i class="fas fa-music"></i>
          <div class="sound-waves">
            <span class="wave wave-1"></span>
            <span class="wave wave-2"></span>
            <span class="wave wave-3"></span>
            <span class="wave wave-4"></span>
            <span class="wave wave-5"></span>
          </div>
        </div>
        <!-- File Name Display -->
        <h3 class="audio-title">{{ model?.original_name || 'Audio File' }}</h3>
        <!-- Audio Metadata -->
        <div class="audio-meta">
          @if (model?.file_type) {
            <span class="meta-item">
              <i class="fas fa-file-audio"></i>
              {{ model.file_type?.toUpperCase() }}
            </span>
          }
          @if (model?.file_type && duration) {
            <span class="meta-divider">•</span>
          }
          @if (duration) {
            <span class="meta-item">
              <i class="fas fa-clock"></i>
              {{ formatTime(duration) }}
            </span>
          }
        </div>
      </div>
    }

    <!-- Hidden Audio Element -->
    <audio
      #audioPlayer
      [src]="getMediaUrl()"
      (loadedmetadata)="onAudioLoaded()"
      (timeupdate)="onTimeUpdate()"
      (play)="onPlay()"
      (pause)="onPause()"
      (waiting)="onWaiting()"
      (canplay)="onCanPlay()"
      (error)="onAudioError()"
      preload="metadata">
      <p>Your browser does not support HTML5 audio.</p>
    </audio>

    <!-- Audio Controls -->
    @if (!isLoading) {
      <div class="audio-controls">
        <!-- Timeline with Waveform Bars -->
        <div class="timeline-container">
          <!-- Decorative Waveform Bars -->
          <div class="waveform-bars">
            @for (bar of waveformBars; track bar; let i = $index) {
              <span class="bar"
                [style.height]="bar + '%'"
                [class.active]="getTimelineProgress() >= (i * 2.5)">
              </span>
            }
          </div>
          <!-- Interactive Timeline -->
          <div
            class="audio-timeline-bar"
            (click)="onTimelineClick($event)"
            (mousedown)="onTimelineDragStart($event)">
            <div class="timeline-buffered" [style.width.%]="getBufferedProgress()"></div>
            <div class="timeline-progress" [style.width.%]="getTimelineProgress()">
              <div class="timeline-thumb"></div>
            </div>
          </div>
        </div>
        <!-- Control Bar -->
        <div class="control-bar">
          <!-- Time Display (Left) -->
          <div class="time-display left">
            {{ formatTime(currentTime) }}
          </div>
          <!-- Center Controls -->
          <div class="center-controls">
            <!-- Skip Backward -->
            <button
              class="control-btn"
              (click)="skip(-10)"
              aria-label="Skip backward 10 seconds"
              matTooltip="Skip -10s (←)">
              <i class="fas fa-backward"></i>
            </button>
            <!-- Play/Pause Button -->
            <button
              class="control-btn play-btn"
              (click)="togglePlay()"
              [attr.aria-label]="isPlaying ? 'Pause' : 'Play'"
              matTooltip="{{ isPlaying ? 'Pause (Space)' : 'Play (Space)' }}">
              <i class="fas" [class.fa-pause]="isPlaying" [class.fa-play]="!isPlaying"></i>
            </button>
            <!-- Skip Forward -->
            <button
              class="control-btn"
              (click)="skip(10)"
              aria-label="Skip forward 10 seconds"
              matTooltip="Skip +10s (→)">
              <i class="fas fa-forward"></i>
            </button>
          </div>
          <!-- Time Display (Right) -->
          <div class="time-display right">
            {{ formatTime(duration) }}
          </div>
        </div>
        <!-- Secondary Control Bar -->
        <div class="secondary-controls">
          <!-- Volume Control -->
          <div class="volume-control">
            <button
              class="control-btn-small"
              (click)="toggleMute()"
              [attr.aria-label]="isMuted ? 'Unmute' : 'Mute'"
              matTooltip="Mute (M)">
              <i class="fas" [class]="'fas ' + getVolumeIcon()"></i>
            </button>
            <input
              type="range"
              class="volume-slider"
              min="0"
              max="1"
              step="0.01"
              [value]="volume"
              (input)="setVolume($any($event.target).value)"
              aria-label="Volume">
          </div>
          <!-- Playback Speed -->
          <div class="speed-control">
            <button
              class="control-btn-small speed-btn"
              [matMenuTriggerFor]="speedMenu"
              aria-label="Playback speed"
              matTooltip="Playback Speed">
              <span class="speed-text">{{ playbackRate }}x</span>
            </button>
            <mat-menu #speedMenu="matMenu" xPosition="before">
              @for (rate of playbackRateOptions; track rate) {
                <button
                  mat-menu-item
                  (click)="setPlaybackRate(rate)"
                  [class.active]="playbackRate === rate">
                  {{ rate }}x
                </button>
              }
            </mat-menu>
          </div>
        </div>
      </div>
    }
  </div>

  <!-- Comment sidebar (slides in over the right) -->
  <div class="audio-comment-drawer" *ngIf="showCommentsSidebar">
    <app-doc-comment-sidebar
      [comments]="comments"
      [activeCommentId]="activeCommentId"
      [showTabs]="false"
      [showAddButton]="true"
      [showStatusPriority]="false"
      (addComment)="startAddComment()"
      (selectComment)="selectComment($event)"
      (addReply)="onCommentReply($event)"
      (updateStatus)="onCommentStatus($event)"
      (updatePriority)="onCommentPriority($event)"
      (deleteComment)="deleteComment($event)"
      (close)="toggleCommentsSidebar()">
    </app-doc-comment-sidebar>
  </div>
  </div><!-- /.audio-main -->

  <!-- Add-comment popup, anchored to the current playback time -->
  <div class="audio-comment-popup" *ngIf="newCommentTime !== null">
    <div class="audio-comment-popup__title">
      <mat-icon>add_comment</mat-icon> Comment at <strong>{{ formatTime(newCommentTime || 0) }}</strong>
    </div>
    <textarea rows="3" [(ngModel)]="newCommentText" placeholder="Write a comment…"></textarea>
    <div class="audio-comment-popup__actions">
      <button type="button" class="audio-comment-popup__btn" (click)="cancelComment()">Cancel</button>
      <button type="button" class="audio-comment-popup__btn is-primary" [disabled]="!newCommentText.trim()"
              (click)="addComment()">Comment</button>
    </div>
  </div>
</div>
